import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { fmt } from "../lib/api";
import { Crown, Globe, Coins, LogOut, User as UserIcon, Gift, Trophy, Shield, Menu } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, lang, currency, logout, changeLang, changeCurrency } = useApp();
  const nav = useNavigate();
  const loc = useLocation();

  const links = [
    { to: "/lobby", k: "nav.lobby" },
    { to: "/blackjack", k: "nav.blackjack" },
    { to: "/roulette", k: "nav.roulette" },
    { to: "/slots", k: "nav.slots" },
    { to: "/instant", label: "⚡ Instant" },
    { to: "/live-casino", label: "🎥 Live" },
    { to: "/sports", k: "nav.sports" },
    { to: "/casinos", label: "🎰 Casinos" },
    { to: "/wheel", label: "Wheel" },
    { to: "/tournaments", label: "Cup" },
    { to: "/leaderboard", label: "Top" },
    { to: "/subscribe", label: "VIP+" },
    { to: "/cashier", k: "nav.cashier" },
  ];

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-20 flex items-center gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <Crown className="text-[#d4af37] group-hover:scale-110 transition" size={30} />
          <div className="font-serif tracking-widest text-xl">
            <span className="gold-text">HAVANA</span>
            <span className="text-[#a40606] ml-1">CASINO</span>
          </div>
          <span className="hidden md:inline-block text-[8px] uppercase tracking-[0.2em] text-emerald-300/80 border border-emerald-400/30 bg-emerald-400/5 rounded px-2 py-1 ml-1 leading-none">Play&nbsp;Money</span>
        </Link>
        {user && (
          <nav className="hidden xl:flex gap-1 ml-4">
            {links.map(l => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1).replace("/","-")}`}
                className={`px-3 py-2 rounded-md text-xs uppercase tracking-wider font-medium transition ${loc.pathname===l.to?"text-[#d4af37]":"text-white/70 hover:text-white"}`}>
                {l.k ? t(lang, l.k) : l.label}
              </Link>
            ))}
            {user.is_admin && (
              <Link to="/admin" data-testid="nav-admin" className={`px-3 py-2 rounded-md text-xs uppercase tracking-wider font-medium transition flex items-center gap-1 ${loc.pathname.startsWith("/admin")?"text-[#d4af37]":"text-[#d4af37]/70 hover:text-[#d4af37]"}`}>
                <Shield size={12}/>Admin
              </Link>
            )}
          </nav>
        )}
        <div className="ml-auto flex items-center gap-2">
          {user?.imperium_plus?.tier && (
            <Link to="/subscribe" data-testid="plus-badge" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-black/40 hover:scale-105 transition" style={{borderColor: user.imperium_plus.tier === "platinum" ? "#b9f2ff" : user.imperium_plus.tier === "gold" ? "#d4af37" : "#c0c0c0"}}>
              <Crown size={12} style={{color: user.imperium_plus.tier === "platinum" ? "#b9f2ff" : user.imperium_plus.tier === "gold" ? "#d4af37" : "#c0c0c0"}}/>
              <span className="text-[10px] uppercase tracking-widest font-bold" style={{color: user.imperium_plus.tier === "platinum" ? "#b9f2ff" : user.imperium_plus.tier === "gold" ? "#d4af37" : "#c0c0c0"}}>
                Plus · {user.imperium_plus.tier}
              </span>
            </Link>
          )}
          {user?.vip_tier && (
            <Link to="/leaderboard" data-testid="vip-badge" className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-black/40 hover:border-[#d4af37]/40 transition">
              <Crown size={12} style={{color:user.vip_tier.color}}/>
              <span className="text-[10px] uppercase tracking-widest" style={{color:user.vip_tier.color}}>{user.vip_tier.name}</span>
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="language-switcher" className="btn-ghost px-3 py-2 rounded-md text-xs flex items-center gap-2">
              <Globe size={14}/> {lang.toUpperCase()}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#121212] border-white/10">
              <DropdownMenuItem data-testid="lang-hu" onClick={()=>changeLang("hu")}>Magyar</DropdownMenuItem>
              <DropdownMenuItem data-testid="lang-en" onClick={()=>changeLang("en")}>English</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="currency-switcher" className="btn-ghost px-3 py-2 rounded-md text-xs flex items-center gap-2">
              <Coins size={14}/> {currency === "HUF" ? "GC" : "PC"}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#121212] border-white/10">
              <DropdownMenuItem data-testid="cur-huf" onClick={()=>changeCurrency("HUF")}>Gold Coins (GC)</DropdownMenuItem>
              <DropdownMenuItem data-testid="cur-eur" onClick={()=>changeCurrency("EUR")}>Premium Coins (PC)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {user ? (
            <>
              <div data-testid="user-balance" className="hidden sm:flex flex-col items-end px-3 py-1.5 rounded-md border border-[#d4af37]/30 bg-black/40">
                <span className="text-[10px] uppercase tracking-widest text-white/50">{t(lang,"lobby.balance")}</span>
                <span className="font-serif text-[#d4af37] text-sm">{fmt(currency==="HUF"?user.balance_huf:user.balance_eur, currency)}</span>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="user-menu" className="btn-ghost rounded-full w-10 h-10 flex items-center justify-center overflow-hidden">
                  {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover"/> : <UserIcon size={18}/>}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#121212] border-white/10">
                  <div className="px-3 py-2 text-xs text-white/60 max-w-[200px] truncate">{user.email}</div>
                  <DropdownMenuSeparator className="bg-white/10"/>
                  <DropdownMenuItem onClick={()=>nav("/lobby")} data-testid="menu-lobby">{t(lang,"nav.lobby")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={()=>nav("/wheel")} data-testid="menu-wheel"><Gift size={12} className="mr-2"/>Daily Wheel</DropdownMenuItem>
                  <DropdownMenuItem onClick={()=>nav("/leaderboard")} data-testid="menu-leaderboard"><Trophy size={12} className="mr-2"/>Leaderboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={()=>nav("/cashier")} data-testid="menu-cashier">{t(lang,"nav.cashier")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={()=>nav("/kyc")} data-testid="menu-kyc"><Shield size={12} className="mr-2"/>Verify Identity (KYC)</DropdownMenuItem>
                  <DropdownMenuItem onClick={()=>nav("/referrals")} data-testid="menu-referrals">🎁 Refer a Friend</DropdownMenuItem>
                  {user.is_admin && <DropdownMenuItem onClick={()=>nav("/admin")} data-testid="menu-admin"><Shield size={12} className="mr-2"/>Admin</DropdownMenuItem>}
                  <DropdownMenuSeparator className="bg-white/10"/>
                  <DropdownMenuItem data-testid="logout-btn" onClick={async()=>{await logout(); nav("/");}} className="text-red-400">
                    <LogOut size={14} className="mr-2"/>{t(lang,"nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn-ghost px-4 py-2 rounded-md text-xs uppercase tracking-wider">{t(lang,"nav.login")}</Link>
              <Link to="/register" data-testid="nav-register" className="btn-gold px-4 py-2 rounded-md text-xs">{t(lang,"nav.register")}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
